import type { InputT } from "@/types/database/forms";
import type { DueEntryT } from "@/types/database/trackers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

// @ts-ignore - Routes might not exist yet
import { Route as ObservationSuccessRoute } from "@/routes/_app/diaries/$diaryId/observation/$observationId/entry/$entryId/page/$pageIndex";
// Import routes with proper type assertions to avoid linter errors
// Use type assertions for routes that might not exist yet
// @ts-ignore - Routes might not exist yet
import { Route as ObservationCompleteRoute } from "@/routes/_app/diary/$diaryId/observation/$observationId/entry/$entryId/complete";
import { toast } from "sonner";
import useHandleNewEntry from "../../entries/hooks/useHandleNewEntry";
import { useSelectInputsByIds } from "../../inputs/api/selectInputsByIds";
import { updateEntryValuesArray } from "../../inputs/hooks/updateEntryValues";
import { useEntryValuesStore } from "../../inputs/hooks/useEntryValuesStore";
import { createDefaultValues } from "../utils/createDefaultValues";
import { createValidationSchema } from "../utils/createValidationSchema";
import { formatEntryValues } from "../utils/formatEntryValues";
import useRepeatableGroupsInputs from "./useRepeatableGroup";

interface Props {
	pageCount: number;
	inputs: InputT[];
	nextEntry: DueEntryT | null;
	formType?: "observation" | "tracker";
	nextPageFn: (pageIndex: number) => void;
	prevPageFn: (pageIndex: number) => void;
	successFn: (id: string) => void;
	completeFn: (id: string, entryId: string) => void;
}

interface NewEntry {
	entry_id: string;
	schedule_id?: string;
	entry_time?: string;
	timing_label?: string;
	time_unit?: string;
	timing_interval?: string;
	schedule_index?: number;
	observation_id?: string;
	tracker_id?: string;
	timing_id?: string | null;
}

const useFormPage = (props: Props) => {
	// @ts-ignore
	const paddedInputs = props.inputs;
	const inputIdsToFetch = useRepeatableGroupsInputs({ inputs: props.inputs });
	const { data: fetchedInputs = [] } = useSelectInputsByIds(inputIdsToFetch);
	// console.log("paddedinputs", paddedInputs);
	const { entryId, observationId, trackerId, diaryId, pageIndex } = useParams({
		strict: false,
	});

	const { handleNewEntry } = useHandleNewEntry(diaryId);

	// Keep track of the entry values across pages in a store
	const updateEntryValuesStore = useEntryValuesStore(
		(state) => state.upsertEntryValues,
	);
	const entryValues = useEntryValuesStore((state) => state.entry_values);

	const nextPage = () => {
		props.nextPageFn(Number(pageIndex) + 1);
	};

	const prevPage = () => {
		props.prevPageFn(Number(pageIndex) - 1);
	};

	// Dynamically create the validation schema and default values for the form
	const DiaryPageSchemaZ = createValidationSchema(props.inputs);
	const defaults = createDefaultValues(props.inputs);

	const form = useForm<z.infer<typeof DiaryPageSchemaZ>>({
		resolver: zodResolver(DiaryPageSchemaZ),
		defaultValues: defaults,
	});

	type DiaryPageSchemaT = z.infer<typeof DiaryPageSchemaZ>;

	const onSubmit: SubmitHandler<DiaryPageSchemaT> = async (formData) => {
		// Debug validation errors if any
		if (Object.keys(form.formState.errors).length > 0) {
			console.error("Form validation errors:", form.formState.errors);

			// Show toast with error details
			for (const [fieldName, error] of Object.entries(form.formState.errors)) {
				const errorMessage = error?.message as string;
				if (errorMessage) {
					const inputName =
						props.inputs.find((i) => i.input_id === fieldName)?.input_label ||
						fieldName;
					toast.error(`Error in field "${inputName}": ${errorMessage}`);
				}
			}

			return;
		}

		try {
			// Convert form data to the expected format
			const formatted = formatEntryValues(
				formData,
				props.inputs,
				entryId,
				fetchedInputs,
			);

			if (Number(pageIndex) < props.pageCount) {
				// If not last page of form, log the entry values in the store and move to the next page
				updateEntryValuesStore(formatted);
				nextPage();
			} else if (Number(pageIndex) === props.pageCount) {
				// If last page of form, handle submit and navigate away
				const newEntry: NewEntry = {
					entry_id: entryId,
					schedule_id: props.nextEntry?.schedule_id,
					entry_time: props.nextEntry?.due_at,
					timing_label: props.nextEntry?.label ?? undefined,
					time_unit: props.nextEntry?.time_unit ?? undefined,
					timing_interval: props.nextEntry?.interval
						? String(props.nextEntry?.interval)
						: undefined,
					schedule_index: props.nextEntry?.schedule_index ?? undefined,
				};

				// Add form-specific fields only if formType and corresponding ID are available
				if (props.formType === "observation" && observationId) {
					newEntry.observation_id = observationId;
				} else if (props.formType === "tracker" && trackerId) {
					newEntry.tracker_id = trackerId;
					newEntry.timing_id = props.nextEntry?.timing_id ?? null;
				}

				if (props.pageCount === 1) {
					// Ensure all required fields are present
					const completeFormatted = formatted.map((item) => ({
						...item,
						created_at: item.created_at || new Date().toISOString(),
						entry_value_id: item.entry_value_id || crypto.randomUUID(),
						last_edited: item.last_edited || new Date().toISOString(),
						number_value:
							item.number_value === undefined || item.number_value === null
								? null
								: item.number_value,
						object_id: item.object_id || null,
						text_array: item.text_array || null,
						text_value: item.text_value || null,
						date_value: item.date_value
							? new Date(item.date_value).toISOString()
							: null,
					}));
					// @ts-ignore - Type mismatch due to date_value handling
					handleNewEntry(newEntry, completeFormatted);
				} else {
					const newEntryValues = updateEntryValuesArray(entryValues, formatted);
					handleNewEntry(newEntry, newEntryValues);
				}

				if (diaryId && entryId) {
					props.completeFn("", entryId);
				} else {
					props.successFn("");
				}
			}
		} catch (error) {
			console.error("Error processing form submission:", error);
			toast.error(
				`Form submission error: ${
					error instanceof Error ? error.message : "Unknown error"
				}`,
			);
		}
	};

	return { form, onSubmit, prevPage, nextPage };
};

export default useFormPage;
